import Reflux from 'reflux';
import Immutable from 'immutable';
import debug from 'debug';
import _ from 'lodash';
import viewSettingsActions from '../actions/viewSettingsActions';
import { prepareFieldId, parseStateFromApi, prepareStateToApi } from '../utils/viewSettings';
import { VIEW_MODES, SETTINGS_KEYS } from '../configs/viewSettings';
import appState from '../appState';

const log = debug('CRM:store:ViewSettings');

let state = Immutable.fromJS({ catalogs: {} });

const DEFAULT_VIEW_SETTINGS = {
  table: {
    sortingRecords: { sortType: -1, fieldId: '#id' },
    fieldsOrder: [],
    groups: [],
    fields: {},
  },
  cards: {
    sortingRecords: { sortType: -1, fieldId: '#id' },
    fieldsOrder: [],
    fields: {},
  },
  calendar: {
    sortingRecords: { sortType: -1, fieldId: '#id' },
    fieldsOrder: [],
    fields: {},
  },
  reports: {},
  pivot: {
    cols: [],
    rows: [],
    vals: [],
    fn: 'count',
  },
  count: {},
  history: {},
  recordHistory: { fields: {} },
};

const viewSettingsStore = Reflux.createStore({
  listenables: [viewSettingsActions],

  changed(params) {
    this.trigger(this, state, params);
    log('changed', params);
  },

  setIn(path, val) {
    state = state.setIn(path, val);
  },

  onLoadViewSettingsCompleted(responseBody, { catalogId, viewId }) {
    const data = responseBody;

    const defaultSettings = DEFAULT_VIEW_SETTINGS;

    if (!data || !Array.isArray(data) || data.length === 0) {
      // Если нет данных с API, используем только дефолты
      viewSettingsActions.mergeViewSettingsToAppState({
        catalogId,
        viewId,
        settings: {
          ...defaultSettings,
          hasPersonalSettings: false,
          hasChanges: false,
        },
      });
      return;
    }

    const [response] = data;
    const { settings, hasPersonalSettings } = response;

    // Мержим дефолты с данными из API (ГЛУБОКИЙ merge, чтобы сохранить вложенные дефолты)
    const parsedSettings = parseStateFromApi(settings);
    const mergedSettings = _.merge({}, defaultSettings, parsedSettings);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        ...mergedSettings,
        hasPersonalSettings,
        hasChanges: false,
      },
    });
  },

  setFieldWidth(params) {
    const { catalogId, viewId, viewMode, fieldId, width } = params;
    const apiFieldId = prepareFieldId(fieldId);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.FIELDS]: { [apiFieldId]: { [SETTINGS_KEYS.WIDTH]: width } },
        },
        hasChanges: true,
      },
    });
  },

  setFieldPinned(params) {
    const { catalogId, viewId, viewMode, fieldId, pinned } = params;
    const apiFieldId = prepareFieldId(fieldId);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.FIELDS]: { [apiFieldId]: { [SETTINGS_KEYS.PINNED]: pinned } },
        },
        hasChanges: true,
      },
    });
  },

  setFieldVisibility(params) {
    const { catalogId, viewId, viewMode, fieldId, visible } = params;
    const apiFieldId = prepareFieldId(fieldId);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.FIELDS]: { [apiFieldId]: { [SETTINGS_KEYS.VISIBLE]: visible } },
        },
        hasChanges: true,
      },
    });
  },

  setAllFieldsVisibility(params) {
    const { catalogId, viewId, viewMode, fieldIds, visible } = params;

    const fields = {};
    fieldIds.forEach((fieldId) => {
      fields[prepareFieldId(fieldId)] = { [SETTINGS_KEYS.VISIBLE]: visible };
    });

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: { [SETTINGS_KEYS.FIELDS]: fields },
        hasChanges: true,
      },
    });
  },

  setFieldsOrder(params) {
    const { catalogId, viewId, viewMode, fieldsOrder } = params;

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.FIELDS_ORDER]: fieldsOrder,
        },
        hasChanges: true,
      },
    });
  },

  setFieldsGrouping(params) {
    const { catalogId, viewId, viewMode, groups } = params;

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.GROUPS]: groups,
        },
        hasChanges: true,
      },
    });
  },

  setRowHeight(params) {
    const { catalogId, viewId, viewMode, rowHeight } = params;

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.ROW_HEIGHT]: rowHeight,
        },
        hasChanges: true,
      },
    });
  },

  setSortingRecords(params) {
    const { catalogId, viewId, viewMode, sortField, sortType } = params;
    const apiFieldId = prepareFieldId(sortField);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [SETTINGS_KEYS.SORTING_RECORDS]: {
            [SETTINGS_KEYS.SORT_TYPE]: sortType,
            [SETTINGS_KEYS.FIELD_ID]: apiFieldId,
          },
        },
        hasChanges: true,
      },
    });
  },

  setOption(params) {
    const { catalogId, viewId, viewMode, option, value } = params;

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        [viewMode]: {
          [option]: value,
        },
        hasChanges: true,
      },
    });
  },

  initViewSettings({ catalogId, viewId }) {
    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: { ...DEFAULT_VIEW_SETTINGS, hasPersonalSettings: false, hasChanges: false },
    });
  },

  batchUpdateSettings(params) {
    const { catalogId, viewId, settings } = params;

    // settings приходит в flat API формате, нужно распарсить
    const parsedSettings = parseStateFromApi(settings);

    viewSettingsActions.mergeViewSettingsToAppState({
      catalogId,
      viewId,
      settings: {
        ...parsedSettings,
        hasChanges: false,
        hasPersonalSettings: false,
      },
    });
  },

  getState() {
    return state;
  },

  getPreparedStateToApi(catalogId) {
    const catalogSettings = appState.getIn(['viewSettings', 'catalogs', catalogId]);
    return prepareStateToApi(catalogSettings.toJS());
  },
});

export default viewSettingsStore;
